/**
 * A thin strip that appears only when the platform is not quietly doing its job.
 *
 * It reads the health endpoint and stays silent otherwise, so an operator learns
 * to trust it. Three states, deliberately worded differently, because they call
 * for different reactions:
 *
 *   - **Dry run** is the platform not acting on the outside world at all. It is
 *     a setting, not a fault, and it is the one thing here that can be true
 *     while every check reports ok, so it gets its own strip and sits on top.
 *     Before this existed the platform had no way at all to say it was not
 *     sending, which made every other reassuring sentence on screen misleading.
 *   - **Degraded** is a check reporting `degraded` rather than failing. The one
 *     that matters today is `embeddings`: with no embedding credential the
 *     platform retrieves brand knowledge through an offline fallback that
 *     answers every query and ranks badly. Nothing errors, drafts just quietly
 *     get vaguer, which is exactly the kind of thing nobody notices for a month.
 *     That is why it is on screen and not only in the health JSON.
 *   - **Failing** is a dependency that is down. The database, the counter store
 *     or the queues answer or they do not.
 *
 * The two health states still share one strip, with the failing wording winning,
 * so the shell never grows an unbounded stack of warnings.
 *
 * Every sentence and the choice of which strips to show live in
 * `SystemBanner.model.ts`, because the workspace has no DOM test environment and
 * copy written here could not be covered. This file is rendering only.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { qk } from '../lib/queryClient'
import type { HealthReport } from '../lib/types'
import { type BannerStrip, type BannerTone, systemBanners } from './SystemBanner.model'
import { IconAlert, IconInfo } from './icons'

const TONE_CLASS: Record<BannerTone, string> = {
  danger: 'border-danger/30 bg-danger-soft text-danger-text',
  warning: 'border-warning/30 bg-warning-soft text-warning-text',
  info: 'border-info/30 bg-info-soft text-info-text',
}

function StripIcon({ tone }: { tone: BannerTone }) {
  const Icon = tone === 'danger' ? IconAlert : IconInfo
  return <Icon className="mt-0.5 h-4 w-4 shrink-0" />
}

function Strip({ strip }: { strip: BannerStrip }) {
  return (
    <div role="status" className={`border-b px-4 py-2 ${TONE_CLASS[strip.tone]}`}>
      <p className="flex items-start gap-2 text-xs leading-relaxed">
        <StripIcon tone={strip.tone} />
        <span>
          <span className="font-semibold">{strip.headline}</span> {strip.body}
        </span>
      </p>
    </div>
  )
}

export function SystemBanner() {
  const { data } = useQuery({
    queryKey: qk.health(),
    queryFn: () => api.get<HealthReport>('/healthz'),
    // Health is nice to know, never load bearing: a failure here shows nothing.
    retry: false,
    staleTime: 60_000,
    refetchInterval: 120_000,
    throwOnError: false,
  })

  const strips = systemBanners(data)
  if (strips.length === 0) return null

  return (
    <>
      {strips.map((strip) => (
        <Strip key={strip.key} strip={strip} />
      ))}
    </>
  )
}
